import { useLiveQuery } from '@electric-sql/pglite-react';
import { blankTemplate, formatRupees, toPaise } from '@quickreport/domain';
import { Badge, Button, Input } from '@quickreport/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { Archive, Copy, Lock, Plus, Search } from 'lucide-react';
import { useDeferredValue, useEffect, useState } from 'react';
import { DEFAULT_PAGE, Pagination } from '../../components/Pagination';
import { useWorkspace } from '../../lib/workspace';
import {
  archiveTemplate,
  duplicateTemplate,
  saveTemplateVersion,
  type TemplateListItem,
  templateCountQuery,
  templateListQuery,
} from '../../local-db/templates';
import { addStarterTemplates, missingStarterTemplates } from '../../local-db/workspace';

export function TemplatesPage() {
  const { db, orgId, engine, can } = useWorkspace();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const deferred = useDeferredValue(q);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE);
  const { sql, params } = templateListQuery(orgId, deferred, { page, pageSize });
  const count = templateCountQuery(orgId, deferred);
  const result = useLiveQuery<TemplateListItem>(sql, params);
  const totalRes = useLiveQuery<{ n: number }>(count.sql, count.params);
  const total = totalRes?.rows[0]?.n ?? 0;
  const rows = result?.rows ?? [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new search or page size starts again from page 1
  useEffect(() => setPage(1), [deferred, pageSize]);
  // If the list gets shorter (a filter, a deletion), stay within the pages that still exist. Using
  // the latest page number here keeps this from undoing a reset to page 1.
  useEffect(() => {
    const last = Math.max(1, Math.ceil(total / pageSize));
    setPage((p) => Math.min(p, last));
  }, [total, pageSize]);

  // Starter templates this lab does not have yet (new ones are added from time to time).
  const [missing, setMissing] = useState<{ key: string; name: string }[]>([]);
  const [adding, setAdding] = useState(false);
  const [addedNote, setAddedNote] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the count of templates changes what is missing
  useEffect(() => {
    let alive = true;
    void missingStarterTemplates(db, orgId).then(
      (m) => alive && setMissing(m.map((x) => ({ key: x.key, name: x.doc.name }))),
    );
    return () => {
      alive = false;
    };
  }, [db, orgId, total]);

  async function addMissing() {
    setAdding(true);
    try {
      const n = await addStarterTemplates(
        db,
        orgId,
        missing.map((m) => m.key),
      );
      void engine.syncNow();
      setAddedNote(
        `Added ${n} template${n === 1 ? '' : 's'}. Check the reference ranges for your analyser.`,
      );
    } finally {
      setAdding(false);
    }
  }

  async function copy(id: string) {
    const copyId = await duplicateTemplate(db, orgId, id);
    void engine.syncNow();
    await navigate({ to: '/templates/$templateId', params: { templateId: copyId } });
  }

  async function archive(id: string, name: string) {
    if (
      !window.confirm(
        `Archive “${name}”? New reports can no longer use it. Reports already made are not changed.`,
      )
    )
      return;
    await archiveTemplate(db, id);
    void engine.syncNow();
  }

  const open = (id: string) =>
    void navigate({ to: '/templates/$templateId', params: { templateId: id } });

  async function createBlank() {
    const { id } = await db.transaction((tx) =>
      saveTemplateVersion(tx, orgId, null, blankTemplate('New template')),
    );
    await navigate({ to: '/templates/$templateId', params: { templateId: id } });
  }

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Templates</h1>
          <p className="mt-1 text-slate">
            Each new report starts from one of these. Editing a template never changes reports
            already made.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button tone="primary" onClick={createBlank}>
            <Plus size={16} /> New template
          </Button>
        </div>
      </div>

      {can('edit_templates') && missing.length > 0 ? (
        <section
          className="mt-5 flex flex-wrap items-center gap-3 rounded-lg border border-brand/25 bg-brand-soft px-4 py-3 text-[13px]"
          aria-label="New starter templates"
        >
          <p className="min-w-0 flex-1">
            <b>
              {missing.length} starter template{missing.length === 1 ? '' : 's'} you do not have
              yet:
            </b>{' '}
            {missing
              .slice(0, 6)
              .map((m) => m.name)
              .join(', ')}
            {missing.length > 6 ? ` and ${missing.length - 6} more` : ''}. Adding them does not
            change your own templates.
          </p>
          <Button size="sm" tone="primary" disabled={adding} onClick={() => void addMissing()}>
            {adding ? 'Adding…' : `Add ${missing.length === 1 ? 'it' : 'them'}`}
          </Button>
        </section>
      ) : null}
      {addedNote ? (
        <p role="status" className="mt-3 rounded-md bg-canvas px-3 py-2 text-[13px] text-slate">
          {addedNote}
        </p>
      ) : null}

      <div className="relative mt-6">
        <Search size={16} className="pointer-events-none absolute top-2 left-2.5 text-slate" />
        <Input
          id="template-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, alias or department, e.g. hemogram, lipid, biochemistry"
          className="h-9 pl-8"
          aria-label="Search templates"
        />
      </div>

      <ul className="mt-3 grid gap-2 md:hidden" aria-label="Templates">
        {rows.map((t) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users open it with the name link inside
          <li
            key={t.id}
            onClick={() => open(t.id)}
            className="flex cursor-pointer items-start gap-2 rounded-xl border border-line bg-paper p-3 active:bg-brand-soft/50"
          >
            <Link
              to="/templates/$templateId"
              params={{ templateId: t.id }}
              onClick={(e) => e.stopPropagation()}
              className="min-w-0 flex-1 text-ink"
            >
              <span className="block leading-snug font-medium">
                {t.name}
                {t.sensitive ? (
                  <Lock size={12} className="ml-1.5 inline text-slate" aria-label="Sensitive" />
                ) : null}
              </span>
              <span className="mt-0.5 block text-[12px] text-slate">
                {[
                  t.department,
                  `${t.tests} field${t.tests === 1 ? '' : 's'}`,
                  can('view_billing') && t.price != null ? formatRupees(toPaise(t.price)) : null,
                  `v${t.current_version}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </Link>
            {can('edit_templates') ? (
              // biome-ignore lint/a11y/noStaticElementInteractions: only keeps these buttons from opening the card
              // biome-ignore lint/a11y/useKeyWithClickEvents: the buttons inside are the keyboard controls
              <span className="flex shrink-0 gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  aria-label={`Duplicate ${t.name}`}
                  className="grid h-10 w-10 place-items-center rounded-lg border border-line text-slate"
                  onClick={() => void copy(t.id)}
                >
                  <Copy size={16} />
                </button>
                <button
                  type="button"
                  aria-label={`Archive ${t.name}`}
                  className="grid h-10 w-10 place-items-center rounded-lg border border-line text-slate"
                  onClick={() => void archive(t.id, t.name)}
                >
                  <Archive size={16} />
                </button>
              </span>
            ) : null}
          </li>
        ))}
        {result && rows.length === 0 ? (
          <li className="rounded-xl border border-dashed border-line bg-paper px-4 py-10 text-center text-slate">
            No template matches “{deferred}”. Try another word, or create a new template.
          </li>
        ) : null}
      </ul>

      <div className="mt-4 hidden overflow-hidden rounded-lg border border-line bg-paper md:block">
        <table className="w-full text-left">
          <thead className="bg-canvas text-[12px] tracking-wide text-slate uppercase">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Department</th>
              <th className="px-4 py-2 text-right font-medium">Fields</th>
              {can('view_billing') ? (
                <th className="px-4 py-2 text-right font-medium">Price</th>
              ) : null}
              <th className="px-4 py-2 text-right font-medium">Version</th>
              <th className="w-20 px-2 py-2">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr
                key={t.id}
                onClick={() => open(t.id)}
                className="cursor-pointer border-t border-line hover:bg-brand-soft/50"
              >
                <td className="px-4 py-2.5">
                  <Link
                    to="/templates/$templateId"
                    params={{ templateId: t.id }}
                    onClick={(e) => e.stopPropagation()}
                    className="font-medium text-ink hover:text-brand"
                  >
                    {t.name}
                  </Link>
                  {t.sensitive ? (
                    <span
                      className="ml-2 inline-flex align-middle"
                      title="Sensitive: never listed in a patient's report list"
                    >
                      <Lock size={13} className="text-slate" />
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2.5 text-slate">{t.department ?? '—'}</td>
                <td className="tabular px-4 py-2.5 text-right">{t.tests}</td>
                {can('view_billing') ? (
                  <td className="tabular px-4 py-2.5 text-right">
                    {t.price == null ? '—' : formatRupees(toPaise(t.price))}
                  </td>
                ) : null}
                <td className="px-4 py-2.5 text-right">
                  <Badge>v{t.current_version}</Badge>
                </td>
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: the buttons inside are the keyboard controls */}
                <td className="px-2 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                  {can('edit_templates') ? (
                    <span className="inline-flex gap-1">
                      <button
                        type="button"
                        aria-label={`Duplicate ${t.name}`}
                        title="Duplicate"
                        className="rounded p-1 text-slate hover:bg-canvas hover:text-ink"
                        onClick={() => void copy(t.id)}
                      >
                        <Copy size={14} />
                      </button>
                      <button
                        type="button"
                        aria-label={`Archive ${t.name}`}
                        title="Archive"
                        className="rounded p-1 text-slate hover:bg-canvas hover:text-flag"
                        onClick={() => void archive(t.id, t.name)}
                      >
                        <Archive size={14} />
                      </button>
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
            {result && rows.length === 0 ? (
              <tr>
                <td
                  colSpan={can('view_billing') ? 6 : 5}
                  className="px-4 py-10 text-center text-slate"
                >
                  No template matches “{deferred}”. Try another word, or create a new template.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
        noun="templates"
      />
    </div>
  );
}
